import {
  CEJM_THEMES,
  GO_CHAPITRES,
  useProfKylianContent,
  type CEJMSousMatiere,
  type FicheItem,
  type Matiere,
  type QuizItem,
  type VideoItem
} from '@/hooks/useProfKylianContent';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useViewedContent } from '@/hooks/useViewedContent';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, router } from 'expo-router';
import {
  ArrowLeft,
  BookOpen,
  CaretDown,
  CaretRight,
  Check,
  FileText,
  Gift,
  GraduationCap,
  Lock,
  PlayCircle,
  Question,
  Rows,
  TrendUp
} from 'phosphor-react-native';
import React, { useMemo, useState } from 'react';
import { Alert, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

const isWeb = Platform.OS === 'web';

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo400: '#A5B4FC',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  amber50: '#FFFBEB',
  emerald600: '#059669',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  emerald50: '#ECFDF5',
  rose500: '#F43F5E',
  rose100: '#FFE4E6',
  purple600: '#7C3AED',
  purple100: '#EDE9FE',
  blue500: '#3B82F6',
  blue100: '#DBEAFE',
};

// Types pour le filtre
type ContentFilter = 'all' | 'fiches' | 'videos' | 'quiz';

export default function ProfKylianScreen() {
  // ===== STATE =====
  const [selectedMatiere, setSelectedMatiere] = useState<Matiere>('CEJM');
  const [selectedFilter, setSelectedFilter] = useState<ContentFilter>('all');
  const [expandedThemes, setExpandedThemes] = useState<string[]>([]);
  const [expandedSousMatieres, setExpandedSousMatieres] = useState<string[]>([]);
  const [expandedChapitres, setExpandedChapitres] = useState<string[]>([]);
  const [showFreeOnly, setShowFreeOnly] = useState(false);

  // ===== HOOKS =====
  const { profile } = useUserProfile();
  const isPremium = profile?.premium === true || profile?.role === 'admin';
  const { fiches, videos, quiz, examens, loading, error } = useProfKylianContent();
  const { markAsViewed, isViewed } = useViewedContent();

  // ===== COMPUTED =====
  
  // Contenus filtrés par matière
  const contentByMatiere = useMemo(() => {
    return {
      CEJM: {
        fiches: fiches.filter(f => f.matiere === 'CEJM'),
        videos: videos.filter(v => v.matiere === 'CEJM'),
        quiz: quiz.filter(q => q.matiere === 'CEJM'),
        examens: examens.filter(e => e.matiere === 'CEJM'),
      },
      'Gestion Opérationnelle': {
        fiches: fiches.filter(f => f.matiere === 'Gestion Opérationnelle'),
        videos: videos.filter(v => v.matiere === 'Gestion Opérationnelle'),
        quiz: quiz.filter(q => q.matiere === 'Gestion Opérationnelle'),
        examens: examens.filter(e => e.matiere === 'Gestion Opérationnelle'),
      },
    };
  }, [fiches, videos, quiz, examens]);

  // Stats par type pour la matière sélectionnée (pour le filtre)
  const contentStatsForMatiere = useMemo(() => {
    const matiereContent = contentByMatiere[selectedMatiere];
    return {
      fiches: matiereContent.fiches.length,
      videos: matiereContent.videos.length,
      quiz: matiereContent.quiz.length,
      total: matiereContent.fiches.length + matiereContent.videos.length + matiereContent.quiz.length,
    };
  }, [contentByMatiere, selectedMatiere]);

  // Stats gratuites PAR MATIÈRE SÉLECTIONNÉE
  const freeStatsForMatiere = useMemo(() => {
    const matiereContent = contentByMatiere[selectedMatiere];
    const freeFiches = matiereContent.fiches.filter(f => f.is_free).length;
    const freeVideos = matiereContent.videos.filter(v => v.is_free).length;
    const freeQuiz = matiereContent.quiz.filter(q => q.is_free).length;
    const freeExamens = matiereContent.examens.filter(e => e.is_free).length;
    return {
      total: freeFiches + freeVideos + freeQuiz + freeExamens,
      fiches: freeFiches,
      videos: freeVideos,
      quiz: freeQuiz,
      examens: freeExamens,
    };
  }, [contentByMatiere, selectedMatiere]);

  // ===== HELPERS =====

  const toggleTheme = (themeId: string) => {
    setExpandedThemes(prev =>
      prev.includes(themeId)
        ? prev.filter(id => id !== themeId)
        : [...prev, themeId]
    );
  };

  const toggleSousMatiere = (key: string) => {
    setExpandedSousMatieres(prev =>
      prev.includes(key)
        ? prev.filter(id => id !== key)
        : [...prev, key]
    );
  };

  const toggleChapitre = (chapId: string) => {
    setExpandedChapitres(prev =>
      prev.includes(chapId)
        ? prev.filter(id => id !== chapId)
        : [...prev, chapId]
    );
  };

  const handleMatiereChange = (matiere: Matiere) => {
    setSelectedMatiere(matiere);
    setSelectedFilter('all'); // Reset filtre quand on change de matière
    setShowFreeOnly(false);
  };

  const isContentAccessible = (item: FicheItem | VideoItem | QuizItem) => {
    return isPremium || item.is_free === true;
  };

  const showPaywall = () => {
    if (isWeb) {
      if (window.confirm('Ce contenu est réservé aux membres Premium.\n\nDébloquer l\'accès illimité ?')) {
        router.push('/paywall');
      }
    } else {
      Alert.alert(
        '👑 Contenu Premium',
        'Ce contenu est réservé aux membres Premium.',
        [
          { text: 'Plus tard', style: 'cancel' },
          {
            text: 'Débloquer ✨',
            style: 'default',
            isPreferred: true,
            onPress: () => router.push('/paywall')
          },
        ]
      );
    }
  };

  const handleContentPress = (item: any, type: string) => {
    if (!isContentAccessible(item)) {
      showPaywall();
      return;
    }

    markAsViewed(item.id, type === 'fiche' ? 'fiches' : type === 'video' ? 'videos' : type === 'quiz' ? 'quiz' : 'examens');

    switch (type) {
      case 'fiche':
        router.push(`/prof-kylian/fiche/${item.id}`);
        break;
      case 'video':
        router.push(`/prof-kylian/videos/${item.id}`);
        break;
      case 'quiz':
        router.push(`/prof-kylian/quiz/${item.id}`);
        break;
      case 'examen':
        router.push(`/prof-kylian/examen/${item.id}`);
        break;
    }
  };

  // Compter les contenus par thème (fiches + vidéos + quiz) EN FONCTION DU FILTRE
  const getThemeContentCount = (themeId: string) => {
    const themeFiches = contentByMatiere.CEJM.fiches.filter(f => f.theme_id === themeId);
    const themeVideos = contentByMatiere.CEJM.videos.filter(v => v.theme_id === themeId);
    const themeQuiz = contentByMatiere.CEJM.quiz.filter(q => q.theme_id === themeId);
    
    switch (selectedFilter) {
      case 'fiches': return themeFiches.length;
      case 'videos': return themeVideos.length;
      case 'quiz': return themeQuiz.length;
      default: return themeFiches.length + themeVideos.length + themeQuiz.length;
    }
  };

  // Compter les contenus gratuits par thème
  const getThemeFreeCount = (themeId: string) => {
    const themeFiches = contentByMatiere.CEJM.fiches.filter(f => f.theme_id === themeId && f.is_free);
    const themeVideos = contentByMatiere.CEJM.videos.filter(v => v.theme_id === themeId && v.is_free);
    const themeQuiz = contentByMatiere.CEJM.quiz.filter(q => q.theme_id === themeId && q.is_free);
    
    switch (selectedFilter) {
      case 'fiches': return themeFiches.length;
      case 'videos': return themeVideos.length;
      case 'quiz': return themeQuiz.length;
      default: return themeFiches.length + themeVideos.length + themeQuiz.length;
    }
  };

  // Compter les contenus par chapitre
  const getChapitreContentCount = (chapId: string) => {
    return contentByMatiere['Gestion Opérationnelle'].fiches.filter(f => f.chapitre_id === chapId).length;
  };

  // Compter les contenus gratuits par chapitre
  const getChapitreFreeCount = (chapId: string) => {
    return contentByMatiere['Gestion Opérationnelle'].fiches.filter(f => f.chapitre_id === chapId && f.is_free).length;
  };

  // Contenus par sous-matière pour un thème (fiches + vidéos + quiz) FILTRÉ
  const getContentBySousMatiere = (themeId: string, sousMatiere: CEJMSousMatiere) => {
    const allFiches = contentByMatiere.CEJM.fiches.filter(f => f.theme_id === themeId && f.sous_matiere === sousMatiere);
    const allVideos = contentByMatiere.CEJM.videos.filter(v => v.theme_id === themeId && v.sous_matiere === sousMatiere);
    const allQuiz = contentByMatiere.CEJM.quiz.filter(q => q.theme_id === themeId && q.sous_matiere === sousMatiere);
    
    return {
      fiches: selectedFilter === 'all' || selectedFilter === 'fiches' ? allFiches : [],
      videos: selectedFilter === 'all' || selectedFilter === 'videos' ? allVideos : [],
      quiz: selectedFilter === 'all' || selectedFilter === 'quiz' ? allQuiz : [],
    };
  };

  const getContentIcon = (type: string, accessible: boolean) => {
    const color = accessible ? colors.indigo600 : colors.slate400;
    switch (type) {
      case 'fiche': return <FileText size={22} color={color} weight="duotone" />;
      case 'video': return <PlayCircle size={22} color={color} weight="duotone" />;
      case 'quiz': return <Question size={22} color={color} weight="duotone" />;
      case 'examen': return <GraduationCap size={22} color={color} weight="duotone" />;
      default: return <FileText size={22} color={color} weight="duotone" />;
    }
  };

  // ===== RENDER FILTER TABS =====
  const renderFilterTabs = () => {
    const filters: { key: ContentFilter; label: string; icon: any; count: number }[] = [
      { key: 'all', label: 'Tout', icon: Rows, count: contentStatsForMatiere.total },
      { key: 'fiches', label: 'Fiches', icon: FileText, count: contentStatsForMatiere.fiches },
      { key: 'videos', label: 'Vidéos', icon: PlayCircle, count: contentStatsForMatiere.videos },
      { key: 'quiz', label: 'Quiz', icon: Question, count: contentStatsForMatiere.quiz },
    ];

    return (
      <View style={styles.filterContainer}>
        <View style={styles.filterRow}>
          {filters.map(filter => {
            const isActive = selectedFilter === filter.key;
            const isDisabled = filter.key !== 'all' && filter.count === 0;
            const Icon = filter.icon;
            
            return (
              <TouchableOpacity
                key={filter.key}
                style={[
                  styles.filterTab,
                  isActive && styles.filterTabActive,
                  isDisabled && styles.filterTabDisabled,
                ]}
                onPress={() => !isDisabled && setSelectedFilter(filter.key)}
                activeOpacity={isDisabled ? 1 : 0.7}
                disabled={isDisabled}
              >
                <Icon 
                  size={16} 
                  color={isActive ? colors.white : isDisabled ? colors.slate300 : colors.slate500} 
                  weight={isActive ? 'bold' : 'regular'} 
                />
                <Text style={[
                  styles.filterTabText,
                  isActive && styles.filterTabTextActive,
                  isDisabled && styles.filterTabTextDisabled,
                ]}>
                  {filter.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // ===== RENDER CONTENT ITEM =====
  const renderContentItem = (item: any, type: string) => {
    const accessible = isContentAccessible(item);
    const viewed = isViewed(item.id);

    return (
      <TouchableOpacity
        key={item.id}
        style={[styles.contentItem, !accessible && styles.contentItemLocked]}
        activeOpacity={0.7}
        onPress={() => handleContentPress(item, type)}
      >
        <View style={[styles.contentIconCircle, !accessible && styles.contentIconCircleLocked]}>
          {getContentIcon(type, accessible)}
        </View>
        
        <View style={styles.contentItemInfo}>
          <Text style={[styles.contentItemTitle, !accessible && styles.contentItemTitleLocked]} numberOfLines={2}>
            {item.title}
          </Text>
          {type === 'video' && item.duration && (
            <Text style={styles.contentItemMeta}>{item.duration}</Text>
          )}
        </View>

        {/* Badges */}
        <View style={styles.contentItemBadges}>
          {item.is_free && !isPremium && (
            <View style={styles.freeBadgeSmall}>
              <Text style={styles.freeBadgeSmallText}>GRATUIT</Text>
            </View>
          )}
          {accessible && viewed && (
            <View style={styles.viewedBadgeSmall}>
              <Check size={12} color={colors.white} weight="bold" />
            </View>
          )}
          {!accessible && (
            <Lock size={20} color={colors.slate400} weight="bold" />
          )}
          {accessible && !viewed && (
            <CaretRight size={20} color={colors.slate400} weight="bold" />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // ===== RENDER CEJM ACCORDEON =====
  const renderCEJMContent = () => {
    return (
      <View style={styles.accordionContainer}>
        {CEJM_THEMES.map((theme, index) => {
          const isExpanded = expandedThemes.includes(theme.id);
          const contentCount = getThemeContentCount(theme.id);
          const freeCount = getThemeFreeCount(theme.id);

          if (contentCount === 0) return null;

          return (
            <View key={theme.id} style={styles.themeBlock}>
              {/* Theme Header */}
              <TouchableOpacity
                style={[styles.themeHeader, isExpanded && styles.themeHeaderExpanded]}
                activeOpacity={0.7}
                onPress={() => toggleTheme(theme.id)}
              >
                <View style={styles.themeHeaderLeft}>
                  <View style={styles.themeNumber}>
                    <Text style={styles.themeNumberText}>{index + 1}</Text>
                  </View>
                  <View style={styles.themeHeaderText}>
                    <Text style={styles.themeTitle} numberOfLines={2}>{theme.name}</Text>
                    <View style={styles.themeMetaRow}>
                      <Text style={styles.themeCount}>{contentCount} contenu{contentCount > 1 ? 's' : ''}</Text>
                      {freeCount > 0 && !isPremium && (
                        <View style={styles.themeFreeIndicator}>
                          <Gift size={14} color={colors.emerald500} weight="fill" />
                          <Text style={styles.themeFreeText}>{freeCount} gratuit{freeCount > 1 ? 's' : ''}</Text>
                        </View>
                      )}
                    </View>
                  </View>
                </View>
                <CaretDown
                  size={22}
                  color={colors.slate500}
                  weight="bold"
                  style={{ transform: [{ rotate: isExpanded ? '180deg' : '0deg' }] }}
                />
              </TouchableOpacity>

              {/* Theme Content */}
              {isExpanded && (
                <View style={styles.themeContent}>
                  {(['Economie', 'Droit', 'Management'] as CEJMSousMatiere[]).map(sm => {
                    const smContent = getContentBySousMatiere(theme.id, sm);
                    const smContentCount = smContent.fiches.length + smContent.videos.length + smContent.quiz.length;
                    if (smContentCount === 0) return null;

                    const smKey = `${theme.id}-${sm}`;
                    const isSmExpanded = expandedSousMatieres.includes(smKey);
                    const smFreeCount = smContent.fiches.filter(f => f.is_free).length + smContent.videos.filter(v => v.is_free).length + smContent.quiz.filter(q => q.is_free).length;

                    return (
                      <View key={smKey} style={styles.sousMatiereBlock}>
                        {/* Sous-matière Header */}
                        <TouchableOpacity
                          style={[styles.sousMatiereHeader, isSmExpanded && styles.sousMatiereHeaderExpanded]}
                          activeOpacity={0.7}
                          onPress={() => toggleSousMatiere(smKey)}
                        >
                          <View style={styles.sousMatiereHeaderLeft}>
                            <Text style={styles.sousMatiereTitle}>{sm}</Text>
                            <View style={styles.sousMatiereCountBadge}>
                              <Text style={styles.sousMatiereCountText}>{smContentCount}</Text>
                            </View>
                            {smFreeCount > 0 && !isPremium && (
                              <View style={styles.smFreeIndicator}>
                                <Gift size={12} color={colors.emerald500} weight="fill" />
                              </View>
                            )}
                          </View>
                          <CaretDown
                            size={18}
                            color={colors.slate500}
                            weight="bold"
                            style={{ transform: [{ rotate: isSmExpanded ? '180deg' : '0deg' }] }}
                          />
                        </TouchableOpacity>

                        {/* Sous-matière Content : Fiches + Vidéos + Quiz ensemble */}
                        {isSmExpanded && (
                          <View style={styles.sousMatiereContent}>
                            {smContent.fiches.map(fiche => renderContentItem(fiche, 'fiche'))}
                            {smContent.videos.map(video => renderContentItem(video, 'video'))}
                            {smContent.quiz.map(q => renderContentItem(q, 'quiz'))}
                          </View>
                        )}
                      </View>
                    );
                  })}

                  {/* Contenus sans sous-matière (ex: Méthodologie) */}
                  {(() => {
                    const noSmFiches = (selectedFilter === 'all' || selectedFilter === 'fiches') 
                      ? contentByMatiere.CEJM.fiches.filter(f => f.theme_id === theme.id && !f.sous_matiere) : [];
                    const noSmVideos = (selectedFilter === 'all' || selectedFilter === 'videos')
                      ? contentByMatiere.CEJM.videos.filter(v => v.theme_id === theme.id && !v.sous_matiere) : [];
                    const noSmQuiz = (selectedFilter === 'all' || selectedFilter === 'quiz')
                      ? contentByMatiere.CEJM.quiz.filter(q => q.theme_id === theme.id && !q.sous_matiere) : [];
                    if (noSmFiches.length + noSmVideos.length + noSmQuiz.length === 0) return null;
                    return (
                      <View style={styles.sousMatiereContent}>
                        {noSmFiches.map(fiche => renderContentItem(fiche, 'fiche'))}
                        {noSmVideos.map(video => renderContentItem(video, 'video'))}
                        {noSmQuiz.map(q => renderContentItem(q, 'quiz'))}
                      </View>
                    );
                  })()}
                </View>
              )}
            </View>
          );
        })}

        {/* Examens CEJM (toujours affichés, pas affectés par le filtre) */}
        {contentByMatiere.CEJM.examens.length > 0 && (
          <View style={styles.examensSection}>
            <Text style={styles.examensSectionTitle}>Sujets d'examen corrigés</Text>
            {contentByMatiere.CEJM.examens.map(examen => renderContentItem(examen, 'examen'))}
          </View>
        )}
      </View>
    );
  };

  // ===== RENDER GESTION ACCORDEON =====
  const renderGestionContent = () => {
    return (
      <View style={styles.accordionContainer}>
        {GO_CHAPITRES.map((chapitre) => {
          const isExpanded = expandedChapitres.includes(chapitre.id);
          const contentCount = getChapitreContentCount(chapitre.id);
          const freeCount = getChapitreFreeCount(chapitre.id);
          
          if (contentCount === 0) return null;

          return (
            <View key={chapitre.id} style={styles.chapitreBlock}>
              {/* Chapitre Header */}
              <TouchableOpacity
                style={[styles.chapitreHeader, isExpanded && styles.chapitreHeaderExpanded]}
                activeOpacity={0.7}
                onPress={() => toggleChapitre(chapitre.id)}
              >
                <View style={styles.chapitreHeaderLeft}>
                  <View style={styles.chapitreNumber}>
                    <Text style={styles.chapitreNumberText}>{chapitre.number}</Text>
                  </View>
                  <View style={styles.chapitreHeaderText}>
                    <Text style={styles.chapitreTitle} numberOfLines={2}>{chapitre.name}</Text>
                    <View style={styles.chapitreMetaRow}>
                      <Text style={styles.chapitreCount}>{contentCount} fiche{contentCount > 1 ? 's' : ''}</Text>
                      {freeCount > 0 && !isPremium && (
                        <View style={styles.chapitreFreeIndicator}>
                          <Gift size={14} color={colors.emerald500} weight="fill" />
                          <Text style={styles.chapitreFreeText}>{freeCount} gratuit{freeCount > 1 ? 's' : ''}</Text>
                        </View>
                      )}
                    </View>
                  </View>
                </View>
                <CaretDown
                  size={22}
                  color={colors.slate500}
                  weight="bold"
                  style={{ transform: [{ rotate: isExpanded ? '180deg' : '0deg' }] }}
                />
              </TouchableOpacity>

              {/* Chapitre Content */}
              {isExpanded && (
                <View style={styles.chapitreContent}>
                  {contentByMatiere['Gestion Opérationnelle'].fiches
                    .filter(f => f.chapitre_id === chapitre.id)
                    .map(fiche => renderContentItem(fiche, 'fiche'))}
                </View>
              )}
            </View>
          );
        })}

        {/* Examens Gestion */}
        {contentByMatiere['Gestion Opérationnelle'].examens.length > 0 && (
          <View style={styles.examensSection}>
            <Text style={styles.examensSectionTitle}>Sujets d'examen corrigés</Text>
            {contentByMatiere['Gestion Opérationnelle'].examens.map(examen => renderContentItem(examen, 'examen'))}
          </View>
        )}

        {/* Message si aucun contenu */}
        {contentByMatiere['Gestion Opérationnelle'].fiches.length === 0 && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyStateText}>Aucun contenu disponible pour Gestion Opérationnelle.</Text>
          </View>
        )}
      </View>
    );
  };

  // ===== RENDER FREE CONTENT VIEW =====
  const renderFreeContent = () => {
    const matiereContent = contentByMatiere[selectedMatiere];
    const freeByType = {
      fiches: matiereContent.fiches.filter(f => f.is_free),
      videos: matiereContent.videos.filter(v => v.is_free),
      quiz: matiereContent.quiz.filter(q => q.is_free),
      examens: matiereContent.examens.filter(e => e.is_free),
    };

    return (
      <View style={styles.freeContentContainer}>
        <View style={styles.freeContentHeader}>
          <Gift size={26} color={colors.emerald500} weight="fill" />
          <Text style={styles.freeContentTitle}>Contenus gratuits - {selectedMatiere === 'CEJM' ? 'CEJM' : 'Gestion'}</Text>
        </View>

        {freeByType.fiches.length > 0 && (
          <View style={styles.freeSection}>
            <Text style={styles.freeSectionTitle}>📄 Fiches ({freeByType.fiches.length})</Text>
            {freeByType.fiches.map(fiche => renderContentItem(fiche, 'fiche'))}
          </View>
        )}

        {freeByType.videos.length > 0 && (
          <View style={styles.freeSection}>
            <Text style={styles.freeSectionTitle}>🎬 Vidéos ({freeByType.videos.length})</Text>
            {freeByType.videos.map(video => renderContentItem(video, 'video'))}
          </View>
        )}

        {freeByType.quiz.length > 0 && (
          <View style={styles.freeSection}>
            <Text style={styles.freeSectionTitle}>❓ Quiz ({freeByType.quiz.length})</Text>
            {freeByType.quiz.map(q => renderContentItem(q, 'quiz'))}
          </View>
        )}

        {freeByType.examens.length > 0 && (
          <View style={styles.freeSection}>
            <Text style={styles.freeSectionTitle}>📝 Examens ({freeByType.examens.length})</Text>
            {freeByType.examens.map(examen => renderContentItem(examen, 'examen'))}
          </View>
        )}

        {freeStatsForMatiere.total === 0 && (
          <View style={styles.emptyFree}>
            <Text style={styles.emptyFreeText}>Aucun contenu gratuit disponible pour {selectedMatiere}.</Text>
          </View>
        )}
      </View>
    );
  };

  // ===== LOADING & ERROR =====
  if (loading) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={[styles.container, styles.centered]}>
          <ActivityIndicator size="large" color={colors.indigo600} />
          <Text style={styles.loadingText}>Chargement du contenu...</Text>
        </View>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={[styles.container, styles.centered]}>
          <Text style={styles.errorText}>Erreur : {error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={() => router.back()}>
            <Text style={styles.retryButtonText}>Retour</Text>
          </TouchableOpacity>
        </View>
      </>
    );
  }

  // ===== MAIN RENDER =====
  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />

      <View style={styles.container}>
        {/* Header */}
        <LinearGradient
          colors={[colors.purple600, colors.indigo600]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.header}
        >
          <View style={styles.headerTop}>
            <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
              <ArrowLeft size={24} color={colors.white} weight="bold" />
            </TouchableOpacity>
            <View style={styles.headerTitleContainer}>
              <GraduationCap size={28} color={colors.white} weight="duotone" />
              <Text style={styles.headerTitle}>Prof Kylian</Text>
            </View>
            <View style={{ width: 40 }} />
          </View>

          {/* Matière Tabs */}
          <View style={styles.matiereTabs}>
            <TouchableOpacity
              style={[styles.matiereTab, selectedMatiere === 'CEJM' && styles.matiereTabActive]}
              onPress={() => handleMatiereChange('CEJM')}
              activeOpacity={0.8}
            >
              <BookOpen size={22} color={selectedMatiere === 'CEJM' ? colors.indigo600 : colors.white} weight="duotone" />
              <Text style={[styles.matiereTabText, selectedMatiere === 'CEJM' && styles.matiereTabTextActive]}>CEJM</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.matiereTab, selectedMatiere === 'Gestion Opérationnelle' && styles.matiereTabActive]}
              onPress={() => handleMatiereChange('Gestion Opérationnelle')}
              activeOpacity={0.8}
            >
              <TrendUp size={22} color={selectedMatiere === 'Gestion Opérationnelle' ? colors.indigo600 : colors.white} weight="duotone" />
              <Text style={[styles.matiereTabText, selectedMatiere === 'Gestion Opérationnelle' && styles.matiereTabTextActive]}>Gestion</Text>
            </TouchableOpacity>
          </View>
        </LinearGradient>

        {/* Filter Tabs */}
        {renderFilterTabs()}

        {/* Free Banner (uniquement pour FREE + si gratuits dispo) */}
        {!isPremium && freeStatsForMatiere.total > 0 && (
          <View style={styles.freeBanner}>
            <View style={styles.freeBannerLeft}>
              <Gift size={24} color={colors.emerald500} weight="fill" />
              <View>
                <Text style={styles.freeBannerTitle}>{freeStatsForMatiere.total} contenu{freeStatsForMatiere.total > 1 ? 's' : ''} gratuit{freeStatsForMatiere.total > 1 ? 's' : ''}</Text>
                <Text style={styles.freeBannerSubtitle}>
                  {freeStatsForMatiere.fiches > 0 && `${freeStatsForMatiere.fiches} fiche${freeStatsForMatiere.fiches > 1 ? 's' : ''}`}
                  {freeStatsForMatiere.videos > 0 && ` • ${freeStatsForMatiere.videos} vidéo${freeStatsForMatiere.videos > 1 ? 's' : ''}`}
                  {freeStatsForMatiere.quiz > 0 && ` • ${freeStatsForMatiere.quiz} quiz`}
                </Text>
              </View>
            </View>
            <TouchableOpacity
              style={[styles.freeBannerButton, showFreeOnly && styles.freeBannerButtonActive]}
              onPress={() => setShowFreeOnly(!showFreeOnly)}
            >
              <Text style={[styles.freeBannerButtonText, showFreeOnly && styles.freeBannerButtonTextActive]}>
                {showFreeOnly ? 'Voir tout' : 'Voir gratuits'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Content */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {showFreeOnly ? (
            renderFreeContent()
          ) : (
            <>
              {selectedMatiere === 'CEJM' && renderCEJMContent()}
              {selectedMatiere === 'Gestion Opérationnelle' && renderGestionContent()}
            </>
          )}

          <View style={{ height: 40 }} />
        </ScrollView>
      </View>
    </>
  );
}

// ===== STYLES =====
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: colors.slate500,
  },
  errorText: {
    fontSize: 16,
    color: colors.rose500,
    textAlign: 'center',
    marginBottom: 20,
  },
  retryButton: {
    backgroundColor: colors.indigo600,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
  },
  retryButtonText: {
    color: colors.white,
    fontWeight: '600',
    fontSize: 16,
  },

  // Header
  header: {
    paddingTop: 60,
    paddingBottom: 20,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  backButton: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
  },

  // Matière Tabs
  matiereTabs: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    gap: 12,
  },
  matiereTab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  matiereTabActive: {
    backgroundColor: colors.white,
  },
  matiereTabText: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.white,
  },
  matiereTabTextActive: {
    color: colors.indigo600,
  },

  // Filter Tabs
  filterContainer: {
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.slate200,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  filterRow: {
    flexDirection: 'row',
    gap: 8,
  },
  filterTab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: colors.slate100,
  },
  filterTabActive: {
    backgroundColor: colors.indigo600,
  },
  filterTabDisabled: {
    backgroundColor: colors.slate50,
    opacity: 0.6,
  },
  filterTabText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.slate600,
  },
  filterTabTextActive: {
    color: colors.white,
  },
  filterTabTextDisabled: {
    color: colors.slate300,
  },

  // Free Banner
  freeBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.emerald50,
    paddingHorizontal: 18,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.emerald100,
  },
  freeBannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    flex: 1,
  },
  freeBannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.emerald600,
  },
  freeBannerSubtitle: {
    fontSize: 13,
    color: colors.emerald500,
    marginTop: 2,
  },
  freeBannerButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.emerald500,
  },
  freeBannerButtonActive: {
    backgroundColor: colors.emerald500,
  },
  freeBannerButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.emerald600,
  },
  freeBannerButtonTextActive: {
    color: colors.white,
  },

  // Scroll
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 18,
  },

  // Accordion Container
  accordionContainer: {
    gap: 14,
  },

  // Theme Block (CEJM)
  themeBlock: {
    backgroundColor: colors.white,
    borderRadius: 18,
    overflow: 'hidden',
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 10,
    elevation: 3,
  },
  themeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 18,
    backgroundColor: colors.white,
  },
  themeHeaderExpanded: {
    backgroundColor: colors.indigo50,
    borderBottomWidth: 1,
    borderBottomColor: colors.indigo100,
  },
  themeHeaderLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  themeNumber: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: colors.indigo600,
    alignItems: 'center',
    justifyContent: 'center',
  },
  themeNumberText: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.white,
  },
  themeHeaderText: {
    flex: 1,
  },
  themeTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
    lineHeight: 22,
  },
  themeMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 4,
  },
  themeCount: {
    fontSize: 13,
    color: colors.slate500,
  },
  themeFreeIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  themeFreeText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.emerald500,
  },
  themeContent: {
    padding: 14,
    gap: 10,
  },

  // Sous-matière Block
  sousMatiereBlock: {
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: colors.slate50,
  },
  sousMatiereHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
  },
  sousMatiereHeaderExpanded: {
    backgroundColor: colors.slate100,
  },
  sousMatiereHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  sousMatiereTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.slate700,
  },
  sousMatiereCountBadge: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: colors.slate200,
  },
  sousMatiereCountText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.slate600,
  },
  smFreeIndicator: {
    marginLeft: 4,
  },
  sousMatiereContent: {
    padding: 10,
    paddingTop: 0,
    gap: 8,
  },

  // Chapitre Block (Gestion)
  chapitreBlock: {
    backgroundColor: colors.white,
    borderRadius: 18,
    overflow: 'hidden',
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 10,
    elevation: 3,
  },
  chapitreHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 18,
    backgroundColor: colors.white,
  },
  chapitreHeaderExpanded: {
    backgroundColor: colors.amber50,
    borderBottomWidth: 1,
    borderBottomColor: colors.amber100,
  },
  chapitreHeaderLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  chapitreNumber: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: colors.amber500,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chapitreNumberText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.white,
  },
  chapitreHeaderText: {
    flex: 1,
  },
  chapitreTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
    lineHeight: 22,
  },
  chapitreMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 4,
  },
  chapitreCount: {
    fontSize: 13,
    color: colors.slate500,
  },
  chapitreFreeIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  chapitreFreeText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.emerald500,
  },
  chapitreContent: {
    padding: 14,
    gap: 8,
  },

  // Content Item
  contentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    backgroundColor: colors.white,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.slate200,
  },
  contentItemLocked: {
    opacity: 0.7,
    backgroundColor: colors.slate50,
  },
  contentIconCircle: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: colors.indigo50,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  contentIconCircleLocked: {
    backgroundColor: colors.slate100,
  },
  contentItemInfo: {
    flex: 1,
  },
  contentItemTitle: {
    fontSize: 15,
    fontWeight: '500',
    color: colors.slate900,
    lineHeight: 21,
  },
  contentItemTitleLocked: {
    color: colors.slate500,
  },
  contentItemMeta: {
    fontSize: 13,
    color: colors.slate400,
    marginTop: 3,
  },
  contentItemBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  freeBadgeSmall: {
    backgroundColor: colors.emerald500,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  freeBadgeSmallText: {
    fontSize: 10,
    fontWeight: '800',
    color: colors.white,
    letterSpacing: 0.5,
  },
  viewedBadgeSmall: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.emerald500,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Examens Section
  examensSection: {
    marginTop: 24,
    backgroundColor: colors.white,
    borderRadius: 18,
    padding: 18,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 10,
    elevation: 3,
  },
  examensSectionTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 14,
  },

  // Free Content View
  freeContentContainer: {
    gap: 18,
  },
  freeContentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  freeContentTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.emerald600,
  },
  freeSection: {
    backgroundColor: colors.white,
    borderRadius: 18,
    padding: 18,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 10,
    elevation: 3,
  },
  freeSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate700,
    marginBottom: 14,
  },
  emptyFree: {
    padding: 40,
    alignItems: 'center',
  },
  emptyFreeText: {
    fontSize: 15,
    color: colors.slate500,
    textAlign: 'center',
  },

  // Empty state
  emptyState: {
    padding: 40,
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 18,
  },
  emptyStateText: {
    fontSize: 15,
    color: colors.slate500,
    textAlign: 'center',
  },
});
